import { buildURL } from './apiFetchRequest';

/**
 * Auxiliary stuff
 */
const getRandomThumbnail = (seed, width, height) => `https://picsum.photos/seed/${seed}/${width}/${height}`;

/**
 * Primitive utilities
 */
const getAuthorLink = (video) => {
    if (video.channel) {
        return `/channel/${video.channel.slug}/`;
    }

    if (video.author) {
        return `/accounts/${video.author.uuid}/`;
    }

    return '#';
};

const getAuthorName = (video) => {
    if (video.channel) {
        return video.channel.title;
    }

    if (video.author) {
        return `${video.author.first_name} ${video.author.last_name}`;
    }

    return '';
};

/* TODO: beat_info.html */
const getBeatInfo = () => '';

const getDateCreated = (video) => {
    if (!video.created) {
        return null;
    }

    const date = new Date(video.created);
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const getPrettyComments = (video) => (video.comments ? video.comments.toLocaleString('en-US') : 0);

const getPrettyDuration = (video) => {
    const { duration } = video;

    const time = {
        h: Math.floor(duration / 3600000) % 24,
        m: Math.floor(duration / 60000) % 60,
        s: Math.floor(duration / 1000) % 60,
    };

    const values = [];

    if (time.h > 0) {
        values.push(time.h.toLocaleString('en-US', { minimumIntegerDigits: 2 }));
    }

    values.push(time.m.toLocaleString('en-US', { minimumIntegerDigits: 2 }));
    values.push(time.s.toLocaleString('en-US', { minimumIntegerDigits: 2 }));

    return values.join(':');
};

const getPrettyViews = (video) =>
    video.views ? `${video.views.toLocaleString('en-US')} view${video.views > 1 ? 's' : ''}` : 0;

const getSafeTitle = (video) => {
    if (video.title.length > 69) {
        return `${video.title.substring(0, 69)}...`;
    }

    return video.title;
};

const getTitleTooltip = (video) => {
    if (video.title.length > 69) {
        return `data-toggle="tooltip" data-placement="bottom" title="${video.title}"`;
    }

    return '';
};

const getContributorURL = (contributor) => `/accounts/${contributor.uuid}/`;
const getVideoURL = (video, options) => {
    const params = video.urlParams ?? {};

    if (options?.addURLTimestamp) {
        params.t = Math.round(video.viewed_time / 1000);
    }

    if (video.campaign_data) {
        const campaignParams = video.campaign_data.split('&');

        campaignParams.forEach((pair) => {
            const [key, value] = pair.split('=');

            params[key] = value;
        });
    }

    return buildURL(`/video/${video.slug}/`, params);
};

/**
 * Complex utilities
 */
const getLongContributors = (video, options) => {
    const source = (video.contributors?.length ? video.contributors : [video.author]).filter((el) => !!el);
    const contributorsList = source.map(
        (user) => `<a href="${getContributorURL(user)}">${user.first_name} ${user.last_name}</a>`,
    );

    const prefix = options.includeFeaturing ? `FEATURING ` : '';

    return contributorsList.length ? `${prefix}${contributorsList.join(', ')}` : '';
};

const getLongContentViews = (video, options) => {
    const prettyViews = getPrettyViews(video);
    const numberOfViews =
        video.views > 0
            ? `<li class="list-inline-item mr-2 video-item__number_of_views">
                <span class="fa fa-eye"></span> ${prettyViews}
            </li>`
            : '';
    const numberOfThumbups =
        options?.includeThumbups && video.thumbups > 0
            ? `<li class="list-inline-item mr-2 video-item__number_of_likes">
                ${video.thumbups.toLocaleString('en-US')} <span class="fa fa-thumbs-o-up thumbup-count-icon"></span>
            </li>`
            : '';
    const created = video.created
        ? `<li class="list-inline-item mr-2 video-item__created_date">
            <span class="fa fa-calendar"></span> ${getDateCreated(video)}
        </li>`
        : '';

    return `<div class="mt-2">
        <ul class="list-inline d-inline text-muted">
            ${numberOfViews}
            ${created}
            ${numberOfThumbups}
        </ul>
    </div>`;
};

const getLongThumbnail = (video, options) => {
    if (options?.useRandomThumbnail) {
        return getRandomThumbnail(video.slug, 640, 360);
    }

    if (!video.thumbnail_large) {
        return options?.thumbnailPlaceholder;
    }

    return video.thumbnail_large;
};

const getShortContributors = (video) => {
    const source = (video.contributors?.length ? video.contributors : [video.author]).filter((el) => !!el);
    const contributors = [];

    for (const human of source) {
        const fullName = `${human.first_name} ${human.last_name}`;

        contributors.push(
            `<a href="/accounts/${human.uuid}/" data-toggle="tooltip" data-placement="bottom" title="${fullName}">${human.first_name[0]}. ${human.last_name}</a>`,
        );
    }

    return contributors.length ? `Feat. ${contributors.join(', ')}` : '';
};

const getShortThumbnail = (video, options) => {
    if (!video.thumbnail_small) {
        return options?.thumbnailPlaceholder;
    }

    // Workaround since small thumbnails are blurry
    if (video.thumbnail_small.includes('96x54_q85_crop-96,54')) {
        return video.thumbnail_large;
    }

    return video.thumbnail_small;
};

const getShortContentViews = (video) => {
    const comments = getPrettyComments(video);
    const commentsMarkup = video.comments
        ? `<li class="list-inline-item mr-2 video-item__number_of_comments">${comments} <span class="fa fa-comment"></span></li>`
        : '';

    const dateCreated = getDateCreated(video);
    const dateCreatedMarkup = dateCreated
        ? `<li class="list-inline-item mr-2 pull-right video-item__created_date"><span class="fa fa-calendar"></span> ${dateCreated}</li>`
        : '';

    const { thumbups } = video;
    const thumbupsMarkup = thumbups
        ? `<span class="thumbup-count">${thumbups}</span><span class="fa fa-thumbs-o-up thumbup-count-icon"></span>`
        : '';

    const views = getPrettyViews(video);
    const viewsMarkup = video.views
        ? `<li class="list-inline-item mr-2 video-item__number_of_views"><span class="fa fa-eye"></span> ${views}</li>`
        : '';

    return `<ul class="list-inline d-inline text-muted" style="font-size:90%">
        ${viewsMarkup}
        ${dateCreatedMarkup}
        ${commentsMarkup}
        <li class="list-inline-item video-item__number_of_likes">
            <span id="score">
                <span id="thumbup-${video.id}-count">${thumbupsMarkup}</span>
            </span>
        </li>
    </ul>`;
};

/**
 * Content long
 */
function getLongItemMarkup(video, options) {
    /* TEMPLATE DATA */
    const beatInfo = getBeatInfo(video);
    const contentViews = getLongContentViews(video, options);
    const contributors = getLongContributors(video, options);
    const prettyDuration = getPrettyDuration(video);
    const thumbnail = getLongThumbnail(video, options);
    const videoURL = getVideoURL(video, options);

    /* MARKUP: IMAGE CONTAINER */
    const progressWidth = video.viewed_time ? (video.viewed_time / video.duration) * 100 : 0;
    const markupProgress = `<div class="video-item__progress"><span style="width: ${progressWidth}%;"></span></div>`;
    const markupImageContainer = `<div class="video-item__img-container video-item__img-container-center">
        <a href="${videoURL}" onclick="clickAndDisable(this)">
            <img src="${thumbnail}" class="video-item__img"><span class="video-item__video-duration">${prettyDuration}</span>
            ${options.showViewedProgress ? markupProgress : ''}
        </a>
    </div>`;

    /* MARKUP: INFO */
    const authorContent = `<a href="${getAuthorLink(video)}">${getAuthorName(video)}</a>`;

    const markupFeatured = options.showFeatured ? '<span>FEATURED</span>' : '';
    const markupInfoAuthor = options.showAuthor
        ? `<h3 class="video-item__author">${authorContent}${beatInfo}${markupFeatured}</h3>`
        : '';

    const markupInfo = `<div class="video-item__info d-flex flex-column align-items-start">
        ${markupInfoAuthor}
        <h2 class="video-item__title"><a href="${videoURL}" onclick="clickAndDisable(this)">${video.title}</a></h2>
        <div class="video-item__desc">${contributors}</div>
        <div class="video-item__desc">${contentViews}</div>
    </div>`;

    /* MARKUP: CAMPAIGN */
    const markupCampaign = video.campaign_data ? '<div class="video-item__campaign"></div>' : '';

    /* CONTENT LONG */
    return `<div class="video-item" data-id="${video.id}">${markupCampaign}${markupImageContainer}${markupInfo}</div>`;
}

/**
 * Content short
 */
function getShortItemMarkup(video, options) {
    /* TEMPLATE DATA */
    const { id } = video;
    const authorLink = getAuthorLink(video);
    const authorName = getAuthorName(video);
    const contentViews = getShortContentViews(video);
    const contributors = getShortContributors(video);
    const duration = getPrettyDuration(video);
    const safeTitle = getSafeTitle(video);
    const thumbnail = getShortThumbnail(video, options);
    const titleTooltip = getTitleTooltip(video);
    const videoURL = getVideoURL(video);

    /* Options-related data */
    const finalContentViews = options?.showContentViews ? `<div class="float-right">${contentViews}</div>` : '';

    /* MARKUP: IMAGE CONTAINER */
    const markupImageContainer = `<div class="related-item__img-container">
        <a
            href="${videoURL}"
            class="pull-left img-overlay impression"
            onclick="gtag('event', 'targeted_content_clicked', {'event_category' : 'Clicks', 'event_label' : '${id}' }); clickAndDisable(this);"
        >
            <img src="${thumbnail}" class="related-item__img">
            <span class="overlay video-duration">${duration}</span>
        </a>
    </div>`;

    /* MARKUP: INFO */
    const markupInfoAuthor = `<div class="related-item__author">
        <a href="${authorLink}">${authorName}</a>
    </div>`;

    const markupInfoTitle = `<h3 class="related-item__title" ${titleTooltip}>
        <a href="${videoURL}" onclick="gtag('event', 'targeted_content_clicked', { 'event_category' :'Clicks', 'event_label' : '${id}'});">
            ${safeTitle}
        </a>
    </h3>`;

    const markupInfoFeaturing = `<div class="related-item__desc">
        ${contributors}
        ${finalContentViews}
    </div>`;

    const markupInfo = `<div class="related-item__info">
        ${markupInfoAuthor}
        ${markupInfoTitle}
        ${markupInfoFeaturing}
    </div>`;

    /* CONTENT SHORT */
    return `<div class="related-item">${markupImageContainer}${markupInfo}</div>`;
}

function getMarkup(type, video, options) {
    if (type === 'short') {
        return getShortItemMarkup(video, options?.short);
    }

    if (type === 'long') {
        const defaults = {
            includeFeaturing: true,
            includeThumbups: true,
            showAuthor: false,
            showFeatured: false,
            showViewedProgress: false,
            addURLTimestamp: false,
        };

        return getLongItemMarkup(video, Object.assign(defaults, options?.long));
    }

    return null;
}

/**
 * Get an HTML component for `content_long` and `content_short` snippets representing a single video.
 *
 * @param {'short'|'long'} type Visual type of video item, `long` or `short`. Port of Django `content_long` and
 *                              `content_short` snippets. Source Django templates are `video_item_media.html` for
 *                              `long`, and `video_item_short.html` for `short` type.
 * @param {Video} video         Video object (model) containing all relevant video information. Based on the
 *                              `Video` model from the `video/models.py` file.
 *
 *                              Frontend related parameters:
 *                                * `urlParams`, an object representing parameters that should be added to the final
 *                                               video URL. For example `{ source: 'homefeed', idx: 1 }`
 * @param {Object} options      Object containing options for displaying video information.
 *
 *                              Currently supported options:
 *                                * Type 'short'
 *                                  * `showContentViews`: boolean, default value is `false`
 *                                  * `thumbnailPlaceholder`: string, default value is ''
 *                                * Type 'long':
 *                                  * `thumbnailPlaceholder`: string, default value is ''
 *                                  * `includeFeaturing`: boolean, default value is `true`
 *                                                        Whether to include word `Featuring` before list of all contributors.
 *                                  * `includeThumbups`: boolean, default value is `true`
 *                                                       Whether to include word no of thumb ups in video description.
 *                                  * `useRandomThumbnail`: boolean, default value is `false`
 *                                  * `showAuthor`: boolean, default value is `false`
 *                                                  Whether to show the author (channel) above the video title.
 *                                  * `showFeatured`: boolean, default value is `false`
 *                                                    Whether to show text 'FEATURED' below the video thumbnail.
 *                                  * `showViewedProgress`: boolean, defajult value is `false`
 *                                                          Whether to show blue progress bar at the bottom of the thumbnail
 *                                                          indicating viewed progress on the video.
 *                                  * `addURLTimestamp`: boolean, default value is `false`
 *                                                       Set to true if the video link should include timestamp, i.e. `?t=N`
 * @returns {null|Video}
 */
export default function createVideoItem(type, video, options) {
    const markup = getMarkup(type, video, options);

    if (!markup) {
        return null;
    }

    const template = document.createElement('template');
    template.innerHTML = markup;

    return template.content.firstChild;
}

/**
 * Add "NEW" badge to existing video item generated by 'createVideoItem' utility function.
 *
 * @param {HTMLElement} videoItem
 */
export function markVideoItemAsNew(videoItem) {
    const infoElement = videoItem.querySelector('.video-item__info');
    const badgeElement = document.createElement('span');

    badgeElement.innerText = 'New';
    badgeElement.classList.add('video-item__badge-new');

    infoElement.prepend(badgeElement);
}
